<template>
	<div class="inner-page new-case">
		<AppHead title="New Case" />

		<PageHeader 
			title="New Case"
			desc="Open a new case and specify the details"
			:hasButton="false"
			:showHelper="true"
			helpTitle="Creation of a New Case"
			helpDesc="Please add a description which will help you to know a brief overview of this case. In addition you can assign the appropriate users to this case."
			/>

		<CaseProgressBar :step="0" />

		<ValidationErrors />

		<div class="new-case-form">

			<InputGroup
				v-if="['admin'].includes($page.props.user.user_type) || ['rep'].includes($page.props.user.tag)"
				title="Assign a primary surgeon"
				desc="You must assign a primary surgeon to this case, they will be the owner of the case."
				>

				<MultiSelect
					v-model="formData.primary_surgeon"
					:options="surgeons"
					:singleSelectionOnly="true"
					/>

			</InputGroup>

			<InputGroup
				v-if="['admin'].includes($page.props.user.user_type) || ['surgeon'].includes($page.props.user.tag)"
				title="Assign a rep"
				desc="You must assign a rep who will manage this case."
				>

				<MultiSelect
					v-model="formData.rep"
					:options="reps"
					:singleSelectionOnly="true"
					/>

			</InputGroup>
			
			<InputGroup
				title="Give a description"
				desc="Please describe this case in less than 100 characters"
				>

				<InputTextarea
					label="Description"
					v-model="formData.description"
					maxLength="100"
					/>

			</InputGroup>
			
			<InputGroup
				title="Assign patient reference"
				desc="This should be anonymised and traceable to the patient at your hospital"
				>

				<InputText
					label="Patient reference"
					type="text"
					placeholder="Please supply a patient reference if you have one"
					v-model="formData.patient_reference"
					/>

			</InputGroup>

			<InputGroup
				v-if="['admin'].includes($page.props.user.user_type) || ['rep'].includes($page.props.user.tag)"
				title="Assign Supporting Surgeons"
				desc="You can assign supporting surgeons here. They will be able to view and edit this case just like the primary surgeon, they will also receive notifications about this case."
				>

				<MultiSelect
					v-model="formData.supporting_surgeons"
					:options="supportingSurgeonsList"
					/>

			</InputGroup>

			<InputGroup
				v-if="['admin'].includes($page.props.user.user_type) || ['rep'].includes($page.props.user.tag)"
				title="Assign viewers"
				desc="You can assign 'viewer' user types here. They will only be able to view the case, they will also receive notifications about this case."
				>

				<MultiSelect
					v-model="formData.viewers"
					:options="viewersList"
					:showAsTags="['case_relation']"
					/>

			</InputGroup>

			

			<InputGroup
				title=""
				desc=""
				:skipWhiteBox="true"
				:noLine="true"
				>
				<Button
					label="Next"
					color="green"
					size="large"
					icon="save" 
					@click="submit"
					/>
			</InputGroup>

		</div>

	</div>
</template>

<script>

import AppLayout from "@/Layouts/AppLayout.vue"
import AppHead from "@/Components/AppHead"
import PageHeader from "@/Components/PageHeader"
import Button from "@/Components/Button"
import ButtonDownload from "@/Components/ButtonDownload"
import InputGroup from "@/Components/InputGroup"
import CaseProgressBar from "@/Components/Cases/CaseProgressBar"
import InputText from "@/Components/InputText"
import InputTextarea from "@/Components/InputTextarea"
import InputFancyRadio from "@/Components/InputFancyRadio"
import MultiSelect from "@/Components/MultiSelect"
import ValidationErrors from "@/Components/ValidationErrors"

export default {
	components: {
		AppLayout,
		AppHead,
		PageHeader,
		Button,
		ButtonDownload,
		InputGroup,
		CaseProgressBar,
		InputText,
		InputTextarea,
		InputFancyRadio,
		MultiSelect,
		ValidationErrors
	},
	props: [
		'reps',
		'viewers',
		'surgeons',
		'preselectedViewers'
	],
	layout: AppLayout,
	data: () => {
		return {
			formData: {
				description: '',
				patient_reference: '',
				primary_surgeon: [],
				viewers: [],
				supporting_surgeons: [],
				rep: []
			}
		}
	},
	methods: {
		submit(){
			this.$inertia.post('/cases/new-case', this.formData)
			// console.log('submitting', this.formData)
		}
	},
	computed: {
		supportingSurgeonsList(){
			// this.surgeons is an array of objects. if one of the objects has the value of this.formData.primary_surgeon, add a parameter 'disabled: true' to that object
			const alteredSurgeons = this.surgeons.map(surgeon => {
				if(this.formData.primary_surgeon && surgeon.id === this.formData.primary_surgeon){
					return {
						...surgeon,
						disabled: true
					}
				} else {
					return surgeon
				}
			})
			return alteredSurgeons
		},
		viewersList(){
			// this.viewers is an array of objects. if one of the objects has the value of this.formData.primary_surgeon or this.formData.rep, add a parameter 'disabled: true' to that object
			const alteredViewers = this.viewers.map(viewer => {
				if(this.formData.primary_surgeon && viewer.id === this.formData.primary_surgeon){
					return {
						...viewer,
						case_relation: 'Primary Surgeon',
						disabled: true
					}
				} else if(this.formData.rep && viewer.id === this.formData.rep){
					return {
						...viewer,
						case_relation: 'Rep',
						disabled: true
					}
				} else if(this.formData.supporting_surgeons && this.formData.supporting_surgeons.includes(viewer.id)){
					return {
						...viewer,
						case_relation: 'Supporting Surgeon',
						disabled: true
					}
				} else {
					return viewer
				}
			})
			return alteredViewers
		}
	},
	mounted(){
		if(this.preselectedViewers && this.preselectedViewers.length > 0){
			this.formData.viewers = this.preselectedViewers
		}
	}
};

</script>

<style scoped lang="sass">

.new-case
	.new-case-form
		padding-top: 30px

		select
			width: 100%
			height: 50px

		select[multiple]
			height: 200px

</style>